import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { WeldScan } from 'pages/reporting/reports/raut/rautReportData'
import { useUnitSystem } from 'pages/reporting/shared/contexts/UnitSystemContext'
import React from 'react'
import { GeckoMeasurement } from 'types'

const styles = StyleSheet.create({
  table: {
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    marginTop: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    minHeight: 20,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    minHeight: 35,
  },
  tableCol: {
    borderRightWidth: 1,
    borderRightColor: '#000',
    padding: 3,
    justifyContent: 'center',
  },
  tableColLast: {
    padding: 3,
    justifyContent: 'center',
  },
  tableCellHeader: {
    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  tableCell: {
    fontSize: 6,
    textAlign: 'center',
  },
  tableCellLeft: {
    fontSize: 6,
    textAlign: 'left',
  },
  groupHeader: {
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'center',
    backgroundColor: '#e0e0e0',
    padding: 2,
    borderBottomWidth: 1,
    borderBottomColor: '#000',
  },
})

interface WeldIndicationsTableProps {
  scans: WeldScan[]
}

export const WeldIndicationsTable: React.FC<WeldIndicationsTableProps> = ({
  scans,
}) => {
  const { getGeometricUnit, measurementPrecision } = useUnitSystem()
  const geometricUnit = getGeometricUnit()
  const unitLabel = geometricUnit.id

  const formatMeasurement = (measurement: GeckoMeasurement | undefined) => {
    if (!measurement) return '0.000'
    return measurement.toNumber(geometricUnit).toFixed(measurementPrecision)
  }

  const formatMeasurementAbs = (measurement: GeckoMeasurement | undefined) => {
    if (!measurement) return '0.000'
    return Math.abs(measurement.toNumber(geometricUnit)).toFixed(
      measurementPrecision
    )
  }

  const getCenterlineOffset = (scan: WeldScan) => {
    return `${formatMeasurement(scan.centerlineNeg)}/${formatMeasurement(scan.centerlinePos)}`
  }

  // Filter scans with indications
  const scansWithIndications = scans.filter(
    (scan) => scan.totalIndications > 0 && scan.indications.length > 0
  )

  if (scansWithIndications.length === 0) {
    return null
  }

  // Column widths (percentages)
  const colWidths = {
    location: '6%',
    scanNumber: '8%',
    indNumber: '4%',
    weldStart: '5%',
    weldStop: '5%',
    clOffset: '7%',
    indStart: '5%',
    length: '5%',
    axialPos: '10%',
    depth: '5%',
    height: '5%',
    amplitude: '8%',
    classification: '10%',
    typeOfIndication: '9%',
  }

  return (
    <View style={styles.table}>
      {/* Header Row */}
      <View style={styles.tableHeaderRow}>
        <View style={[styles.tableCol, { width: colWidths.location }]}>
          <Text style={styles.tableCellHeader}>Location</Text>
        </View>
        <View style={[styles.tableCol, { width: colWidths.scanNumber }]}>
          <Text style={styles.tableCellHeader}>Scan #</Text>
        </View>
        <View style={[styles.tableCol, { width: colWidths.indNumber }]}>
          <Text style={styles.tableCellHeader}>Ind{'\n'}#</Text>
        </View>
        <View style={[styles.tableCol, { width: '10%' }]}>
          <View style={styles.groupHeader}>
            <Text>Weld Details ({unitLabel})</Text>
          </View>
          <View style={{ flexDirection: 'row', flex: 1 }}>
            <View
              style={[styles.tableCol, { width: '50%', borderRightWidth: 1 }]}>
              <Text style={styles.tableCellHeader}>Start</Text>
            </View>
            <View style={[styles.tableColLast, { width: '50%' }]}>
              <Text style={styles.tableCellHeader}>Stop</Text>
            </View>
          </View>
        </View>
        <View style={[styles.tableCol, { width: colWidths.clOffset }]}>
          <Text style={styles.tableCellHeader}>
            C/L{'\n'}Offset{'\n'}({unitLabel})
          </Text>
        </View>
        <View style={[styles.tableCol, { width: '38%' }]}>
          <View style={styles.groupHeader}>
            <Text>Indication Details ({unitLabel})</Text>
          </View>
          <View style={{ flexDirection: 'row', flex: 1 }}>
            <View
              style={[
                styles.tableCol,
                { width: '13.16%', borderRightWidth: 1 },
              ]}>
              <Text style={styles.tableCellHeader}>Start</Text>
            </View>
            <View
              style={[
                styles.tableCol,
                { width: '13.16%', borderRightWidth: 1 },
              ]}>
              <Text style={styles.tableCellHeader}>Length</Text>
            </View>
            <View
              style={[
                styles.tableCol,
                { width: '26.32%', borderRightWidth: 1 },
              ]}>
              <Text style={styles.tableCellHeader}>Axial Pos</Text>
            </View>
            <View
              style={[
                styles.tableCol,
                { width: '13.16%', borderRightWidth: 1 },
              ]}>
              <Text style={styles.tableCellHeader}>Depth</Text>
            </View>
            <View
              style={[
                styles.tableCol,
                { width: '13.16%', borderRightWidth: 1 },
              ]}>
              <Text style={styles.tableCellHeader}>Height</Text>
            </View>
            <View style={[styles.tableColLast, { width: '21.05%' }]}>
              <Text style={styles.tableCellHeader}>Amp (%)</Text>
            </View>
          </View>
        </View>
        <View style={[styles.tableCol, { width: colWidths.classification }]}>
          <Text style={styles.tableCellHeader}>Classification</Text>
        </View>
        <View
          style={[styles.tableColLast, { width: colWidths.typeOfIndication }]}>
          <Text style={styles.tableCellHeader}>Type of Indication</Text>
        </View>
      </View>

      {/* Data Rows */}
      {scansWithIndications.map((scan) =>
        scan.indications.map((indication, indIndex) => (
          <View key={`${scan.id}-${indIndex}`} style={styles.tableRow}>
            <View style={[styles.tableCol, { width: colWidths.location }]}>
              <Text style={styles.tableCell}>{scan.scanLocation}</Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.scanNumber }]}>
              <Text style={styles.tableCellLeft}>
                {scan.scanLabel || scan.fileName}
              </Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.indNumber }]}>
              <Text style={styles.tableCell}>
                {indication.indicationNumber}
              </Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.weldStart }]}>
              <Text style={styles.tableCell}>
                {formatMeasurement(scan.scanStart)}
              </Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.weldStop }]}>
              <Text style={styles.tableCell}>
                {formatMeasurement(scan.scanStop)}
              </Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.clOffset }]}>
              <Text style={styles.tableCell}>{getCenterlineOffset(scan)}</Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.indStart }]}>
              <Text style={styles.tableCell}>
                {formatMeasurement(indication.start)}
              </Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.length }]}>
              <Text style={styles.tableCell}>
                {formatMeasurement(indication.length)}
              </Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.axialPos }]}>
              <Text style={styles.tableCell}>
                {formatMeasurement(indication.axialPosition)}
              </Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.depth }]}>
              <Text style={styles.tableCell}>
                {formatMeasurementAbs(indication.depth)}
              </Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.height }]}>
              <Text style={styles.tableCell}>
                {formatMeasurementAbs(indication.height)}
              </Text>
            </View>
            <View style={[styles.tableCol, { width: colWidths.amplitude }]}>
              <Text style={styles.tableCell}>{indication.amplitude}%</Text>
            </View>
            <View
              style={[styles.tableCol, { width: colWidths.classification }]}>
              <Text style={styles.tableCellLeft}>
                {indication.classification}
              </Text>
            </View>
            <View
              style={[
                styles.tableColLast,
                { width: colWidths.typeOfIndication },
              ]}>
              <Text style={styles.tableCellLeft}>Weld Flaw</Text>
            </View>
          </View>
        ))
      )}
    </View>
  )
}
